import { api } from './client';
import { downloadBlob } from './iaaExports';

/** Draft trades (TRADE-1, migration 0117). Admin only. A draft changes nothing in the
 *  portfolio; amounts travel as strings (exact). Design: decisions/2026-10-23_trades-trade-mode-design.md. */

export type TradeUnit = 'quantity' | 'value' | 'pct';
export type SettleDefault = 'ref' | 'sec' | 'other';
export type TradeSide = 'buy' | 'sell';
export type PriceSource = 'own' | 'same_custodian_day' | 'other_custodian_day' | 'securities' | 'same_custodian_last' | 'typed';

export interface TradeLineIn {
  security_id: string | null;
  instrument_code?: string | null;
  /** null = a held not-approved security kept (needs keep_note); 'transfer' = cash moved between
   *  two of the portfolio's cash accounts (TP-XFER, migration 0119). */
  side: TradeSide | 'transfer' | null;
  amount_entered?: string | null;
  quantity?: string | null;
  price?: string | null;
  price_source?: PriceSource | null;
  price_date?: string | null;
  accrued_ref?: string | null;
  settle_account?: string | null;
  keep_note?: string | null;
  to_account?: string | null;       // TP-XFER: the account receiving the cash
  to_amount?: string | null;        // … in its currency (quantity = the amount leaving, from-account ccy)
  /** TRADE-CONS 4c (0131): the receiving portfolio (ref) when it is ANOTHER portfolio of the run. */
  to_portfolio?: string | null;
}

export interface TradeDraftIn {
  portfolio: string;
  base_draft_id?: string | null;
  unit: TradeUnit;
  settle_default: SettleDefault;
  settle_default_account?: string | null;
  title?: string | null;
  lines: TradeLineIn[];
}

export interface TradeLineOut extends TradeLineIn {
  id: string; isin: string | null; name: string | null; currency: string | null;
}

export interface DependentDraft { id: string; title: string; problems: string[] }

export interface TradeDraft {
  id: string; portfolio: string; title: string | null; unit: TradeUnit;
  settle_default: SettleDefault; settle_default_account: string | null;
  base_draft_id: string | null; base_title: string | null;
  as_of_date: string; latest_positions_date: string | null; newer_positions: boolean;
  line_count: number; updated_at: string;
  /** TRADES-RM: who saved it last (drafts are shared per portfolio). */
  updated_by_name?: string | null;
  /** TQ12: what no longer holds over its base as it is now. */
  problems: string[];
  dependents?: DependentDraft[] | null;
  lines?: TradeLineOut[] | null;
  /** TRADE-LIST (0120): the serial, who created it, the three ticks with who / when, the lock. */
  transfer_count?: number;
  serial?: string;
  created_at?: string | null; created_by_name?: string | null;
  client_pending_at?: string | null; client_pending_by_name?: string | null;
  sent_at?: string | null; sent_by_name?: string | null;
  executed_at?: string | null; executed_by_name?: string | null;
  locked?: boolean;
  documents_at?: string | null;   // N3: when the stored PDF / Excel were made (null = none yet)
  portfolio_label?: string | null; agreement_id?: string | null; agreement_label?: string | null;
  /** TRADE-FLOW (0122): the lifecycle. `client_pending_*` = emailed to the client. */
  status?: TradeStatus; status_label?: string;
  approved_at?: string | null; approved_by_name?: string | null; approved_note?: string | null;
  rejected_at?: string | null; rejected_by_name?: string | null;
  cancelled_at?: string | null; cancelled_by_name?: string | null; cancel_reason?: string | null;
  client_email?: TradeFileRef | null; bank_email?: TradeFileRef | null; approval_file?: TradeFileRef | null;
  revised_from?: { id: string; serial: string } | null; revised_as?: { id: string; serial: string } | null;
  /** TRADE-CONS 4a (0130): the run it belongs to (a consolidation traded as one); changed only through it (O8). */
  run_id?: string | null; run_serial?: string | null;
}

/** TRADE-CONS step 4 (0130): a consolidation traded as one — one draft per member portfolio, saved
 *  together; serial TRC- for the client, each draft keeps its TR- (S4-Q5). `trades.draft`. */
export interface TradeRunPortfolioIn {
  portfolio: string; settle_default: SettleDefault; settle_default_account?: string | null; lines: TradeLineIn[];
}
export interface TradeRunIn {
  consolidation_id: string; unit: TradeUnit; title?: string | null; portfolios: TradeRunPortfolioIn[];
  /** 5c (S5-Q10): the rules and goal order the split was optimized with (null = not optimized). */
  optimizer_used?: OptimizerUsed | null;
}
/** 5c: what a run's split was optimized with, kept with the run. */
export interface OptimizerUsed {
  preset: string; preset_label: string; ranking: string[]; tolerance_bps: number; at: string;
  members: { label: string; min_cash: number; ccy: string; wrapper: string | null }[];
}
/** 5b (`POST /trades/optimize`): a consolidation's instructed rows, in the run's currency. */
export interface OptimizeIn {
  gross: number; tolerance_bps: number; ranking: string[];
  members: { ref: string; label: string; bank: string | null; wrapper: string | null; cash: number; min_cash: number }[];
  securities: { key: string; name: string; approved: boolean; increment: number; min_piece: number; target_value: number;
    holdings: { portfolio: string; held: number; unit_value: number | null; gain_per_unit: number | null }[] }[];
}
export interface OptimizeOut {
  status: 'optimal' | 'feasible' | 'infeasible';
  trades: { security: string; portfolio: string; side: 'buy' | 'sell'; quantity: number; value: number; all?: boolean }[];
  transfers: { from: string; to: string; amount: number }[];
  goals: { goal: string; label: string; value: number }[];
  exceptions: string[];
  seconds: number;
}
export interface TradeRun {
  id: string; serial: string; agreement_id: string; agreement_label: string | null;
  consolidation_id: string; consolidation_name: string | null; consolidation_deleted: boolean;
  title: string | null; unit: TradeUnit; status: TradeStatus; status_label: string; locked: boolean;
  created_at: string; created_by_name?: string | null; updated_at: string; updated_by_name?: string | null;
  drafts: TradeDraft[];
  /** 4d (0132): when the stored documents were made, and the files (the client PDF, an Excel per bank). */
  documents_at?: string | null;
  files?: TradeRunFile[];
  optimizer_used?: OptimizerUsed | null;
  /** 4e (0133): the run's steps (its drafts follow), the stored emails / evidence, each bank's sending. */
  client_pending_at?: string | null; client_pending_by_name?: string | null;
  approved_at?: string | null; approved_by_name?: string | null; approved_note?: string | null;
  rejected_at?: string | null; rejected_by_name?: string | null;
  cancelled_at?: string | null; cancelled_by_name?: string | null; cancel_reason?: string | null;
  client_email?: TradeFileRef | null; approval_file?: TradeFileRef | null;
  revised_from?: { id: string; serial: string } | null; revised_as?: { id: string; serial: string } | null;
  banks?: TradeRunBank[];
}
export interface TradeRunFile { id: string; kind: 'client_pdf' | 'bank_xlsx'; filename: string; bank: string | null }
/** 4e (S4-Q6): one custodian bank of the run — its portfolios, its Excel, and its email once sent. */
export interface TradeRunBank {
  bank_id: string; bank: string; bank_name: string | null; portfolios: string[]; excel_file_id: string | null;
  sent_at: string | null; sent_by_name: string | null; email_file: TradeFileRef | null;
}
/** 4e: who the run's client email and each bank's email may go to. */
export interface TradeRunEmailOptions {
  client: TradeEmailOptions['client'];
  banks: { bank_id: string; bank: string | null; to: TradeContact[]; cc: TradeContact[] }[];
}
/** 4d: a portfolio's figures twice — `ref` in its own Ref Ccy (its bank Excel tab), `shown` in the
 *  run's currency (the client PDF) — and the cash it receives from another portfolio of the run. */
export interface TradeRunDocPart {
  portfolio: string; ref: TradeDocIn; shown: TradeDocIn;
  incoming: { from_portfolio: string; settle_account: string; to_account: string; to_amount: string;
    to_currency: string | null; value_ref: string | null }[];
}
export interface TradeRunDocIn {
  ccy: string; parts: TradeRunDocPart[];
  /** Pedro 2026-10-30: the whole consolidation's Before and after (the run's currency). */
  consolidated?: { summary: TradeDocLine[]; gross_before: string | null; gross_after: string | null } | null;
}

export type TradeStatus = 'draft' | 'with_client' | 'approved' | 'sent' | 'executed' | 'cancelled' | 'revised';
export interface TradeFileRef { id: string; filename: string; at: string | null; size: number }

/** TRADE-FLOW: who the two emails may go to. */
export interface TradeEmailOptions {
  client: { agreement_label: string | null; kyc_contact_id: string | null;
    recipients: { person_id: string; name: string; roles: string; email: string | null }[] };
  bank: { bank: string | null; to: TradeContact[]; cc: TradeContact[] };
}
export interface TradeContact { id: string; name: string; email: string | null }

/** A price for a security not held (TQ3 / TQ3a). `source` null = none found (type one). */
export interface TradePrice {
  source: PriceSource | null; date: string; price: string | null;
  factor: string | null; factor_guessed: boolean;
  accrued_per_unit: string | null; accrued_currency: string | null;
}

/** TRADE-DOC: the trade document of a saved draft (orders = its saved lines + the screen's figures). */
export interface TradeDocOrder {
  security_id: string | null; side: TradeSide | 'transfer'; name: string | null; isin: string | null;
  bb_ticker: string | null; currency: string | null; quantity: string; price: string | null;
  value_sec: string | null; accrued_sec: string | null;   // TRADE-DOC v2: security's currency
  value_ref: string | null; settle_account: string | null;
  // TP-XFER: a transfer leaves settle_account (quantity, `currency`) and reaches to_account.
  to_account?: string | null; to_amount?: string | null; to_currency?: string | null;
  /** 4c / 4d: the receiving portfolio of a transfer, when another of the run. */
  to_portfolio?: string | null;
}
export interface TradeDocLine {
  security_id: string | null; is_cash: boolean; groups: string[];   // TP-PDF: TER, grouping
  name: string | null; isin: string | null; qty_before: string | null; value_before: string | null;
  pct_before: string | null; qty_after: string | null; value_after: string | null; pct_after: string | null;
}
export interface TradeDocIn {
  format: 'pdf' | 'xlsx'; orders: TradeDocOrder[];
  buy_total: string | null; sell_total: string | null; net_total: string | null;
  summary: TradeDocLine[]; gross_before: string | null; gross_after: string | null;
}

async function document(id: string, body: TradeDocIn): Promise<void> {
  const res = await fetch(`/trades/drafts/${id}/document`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail: unknown = null;
    try { detail = (await res.json()).detail; } catch { /* non-JSON body */ }
    throw new Error(typeof detail === 'string' ? detail : 'The trade document could not be produced.');
  }
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  downloadBlob(await res.blob(), m ? m[1] : `trade-orders.${body.format}`);
}

export const tradesApi = {
  document,
  price: (portfolio: string, securityId: string) =>
    api.get<TradePrice>(`/trades/price?portfolio=${encodeURIComponent(portfolio)}&security_id=${encodeURIComponent(securityId)}`),
  list: (portfolio: string) =>
    api.get<{ items: TradeDraft[] }>(`/trades/drafts?portfolio=${encodeURIComponent(portfolio)}`),
  get: (id: string) => api.get<TradeDraft>(`/trades/drafts/${id}`),
  create: (body: TradeDraftIn) => api.post<TradeDraft>('/trades/drafts', body),
  // N5: `override` = the Admin override (Beta); the server honours it for Admins only.
  save: (id: string, body: TradeDraftIn, override = false) =>
    api.put<TradeDraft>(`/trades/drafts/${id}${ov(override)}`, body),
  remove: (id: string, override = false) => api.delete<{ moved: DependentDraft[] }>(`/trades/drafts/${id}${ov(override)}`),
  /** TRADE-LIST: every draft, or those of the portfolios linked to an agreement (`portfolios.read`). */
  listAll: (agreementId?: string) =>
    api.get<{ items: TradeDraft[] }>(`/trades/list${agreementId ? `?agreement_id=${encodeURIComponent(agreementId)}` : ''}`),
  /** N3: render and keep the saved draft's PDF and Excel (replacing the previous pair). */
  storeDocuments: (id: string, body: TradeDocIn, override = false) =>
    api.post<TradeDraft>(`/trades/drafts/${id}/documents${ov(override)}`, body),
  openStored,
  // ── TRADE-FLOW (0122): each step commits at once (its own confirm), `trades.draft` ──
  emailOptions: (id: string) => api.get<TradeEmailOptions>(`/trades/drafts/${id}/email-options`),
  /** Prepare the client email (unsent .eml, the PDF attached): downloaded, and returned so the
   *  same file is stored on "Yes, sent". */
  prepareClientEmail: (id: string, personIds: string[]) => eml(`/trades/drafts/${id}/client-email`, { person_ids: personIds }),
  clientEmailSent: (id: string, file: File) => upload<TradeDraft>(`/trades/drafts/${id}/client-email/sent`, { file }),
  prepareBankEmail: (id: string, contactIds: string[]) => eml(`/trades/drafts/${id}/bank-email`, { contact_ids: contactIds }),
  bankEmailSent: (id: string, file: File) => upload<TradeDraft>(`/trades/drafts/${id}/bank-email/sent`, { file }),
  approve: (id: string, note: string, evidence: File | null) =>
    upload<TradeDraft>(`/trades/drafts/${id}/approve`, { note, ...(evidence ? { evidence } : {}) }),
  reject: (id: string, reason: string, then: 'cancel' | 'revise') =>
    api.post<{ draft: TradeDraft; new_draft: TradeDraft | null }>(`/trades/drafts/${id}/reject`, { reason, then }),
  execute: (id: string) => api.post<TradeDraft>(`/trades/drafts/${id}/execute`, {}),
  cancel: (id: string, reason: string) => api.post<TradeDraft>(`/trades/drafts/${id}/cancel`, { reason }),
  /** A stored email (.eml, opens in Outlook) or the approval evidence. */
  openFile: (id: string, f: TradeFileRef) => download(`/trades/drafts/${id}/files/${f.id}`, f.filename),
  /** 5b: split a consolidation's instructed rows across its portfolios (a suggestion; nothing stored). */
  optimize: (body: OptimizeIn) => api.post<OptimizeOut>('/trades/optimize', body),
  // ── TRADE-CONS step 4 (0130): runs ──
  runs: {
    list: (agreementId?: string) =>
      api.get<{ items: TradeRun[] }>(`/trades/runs${agreementId ? `?agreement_id=${encodeURIComponent(agreementId)}` : ''}`),
    get: (id: string) => api.get<TradeRun>(`/trades/runs/${id}`),
    create: (body: TradeRunIn) => api.post<TradeRun>('/trades/runs', body),
    save: (id: string, body: TradeRunIn, override = false) => api.put<TradeRun>(`/trades/runs/${id}${ov(override)}`, body),
    remove: (id: string, override = false) => api.delete<void>(`/trades/runs/${id}${ov(override)}`),
    /** 4d: render and keep the run's client PDF and its Excel per bank (replacing the previous set). */
    storeDocuments: (id: string, body: TradeRunDocIn, override = false) =>
      api.post<TradeRun>(`/trades/runs/${id}/documents${ov(override)}`, body),
    /** 4d: a stored document of the run, downloaded. */
    openFile: (runId: string, f: TradeRunFile | TradeFileRef) => download(`/trades/runs/${runId}/files/${f.id}`, f.filename),
    // ── 4e (0133): the run's steps, as a trade's (each commits at once, its own confirm) ──
    emailOptions: (id: string) => api.get<TradeRunEmailOptions>(`/trades/runs/${id}/email-options`),
    prepareClientEmail: (id: string, personIds: string[]) => eml(`/trades/runs/${id}/client-email`, { person_ids: personIds }),
    clientEmailSent: (id: string, file: File) => upload<TradeRun>(`/trades/runs/${id}/client-email/sent`, { file }),
    approve: (id: string, note: string, evidence: File | null) =>
      upload<TradeRun>(`/trades/runs/${id}/approve`, { note, ...(evidence ? { evidence } : {}) }),
    reject: (id: string, reason: string, then: 'cancel' | 'revise') =>
      api.post<{ run: TradeRun; new_run: TradeRun | null }>(`/trades/runs/${id}/reject`, { reason, then }),
    prepareBankEmail: (id: string, bankId: string, contactIds: string[]) =>
      eml(`/trades/runs/${id}/banks/${bankId}/email`, { contact_ids: contactIds }),
    bankEmailSent: (id: string, bankId: string, file: File) => upload<TradeRun>(`/trades/runs/${id}/banks/${bankId}/email/sent`, { file }),
    execute: (id: string, draftId: string) => api.post<TradeRun>(`/trades/runs/${id}/drafts/${draftId}/execute`, {}),
    cancel: (id: string, reason: string) => api.post<TradeRun>(`/trades/runs/${id}/cancel`, { reason }),
  },
};

function ov(on: boolean) { return on ? '?admin_override=true' : ''; }

async function detailOf(res: Response, fallback: string): Promise<string> {
  try { const d = (await res.json()).detail; return typeof d === 'string' ? d : fallback; } catch { return fallback; }
}

function fileName(res: Response, fallback: string): string {
  const m = /filename="?([^";]+)"?/i.exec(res.headers.get('Content-Disposition') ?? '');
  return m ? m[1] : fallback;
}

/** POST JSON, download the .eml that comes back, and return it as a File. */
async function eml(path: string, body: unknown): Promise<File> {
  const res = await fetch(path, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error(await detailOf(res, 'The email could not be prepared.'));
  const blob = await res.blob();
  const name = fileName(res, 'trade.eml');
  downloadBlob(blob, name);
  return new File([blob], name, { type: 'message/rfc822' });
}

async function upload<T>(path: string, fields: Record<string, string | File>): Promise<T> {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) form.append(k, v);
  const res = await fetch(path, { method: 'POST', credentials: 'include', body: form });
  if (!res.ok) throw new Error(await detailOf(res, 'Not saved.'));
  return res.json();
}

async function download(path: string, fallback: string): Promise<void> {
  const res = await fetch(path, { credentials: 'include' });
  if (!res.ok) throw new Error(await detailOf(res, 'The file could not be opened.'));
  downloadBlob(await res.blob(), fileName(res, fallback));
}

/** N3 / N4: a draft's stored document, downloaded — the PDF too, like the Excel (TF-PDF-DL, Pedro
 *  2026-10-25: "let's have PDF download that document as well, just like Excel"). */
async function openStored(id: string, fmt: 'pdf' | 'xlsx'): Promise<void> {
  const res = await fetch(`/trades/drafts/${id}/documents/${fmt}`, { credentials: 'include' });
  if (!res.ok) {
    let detail: unknown = null;
    try { detail = (await res.json()).detail; } catch { /* non-JSON body */ }
    throw new Error(typeof detail === 'string' ? detail : 'The document could not be opened.');
  }
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  downloadBlob(await res.blob(), m ? m[1] : `trade-orders.${fmt}`);
}
